/**
 * Voice calling types and constants.
 * Used by the Twilio Voice SDK integration for browser-based calling.
 */

// ── Call Status ─────────────────────────────────────────────────────

export type CallStatus =
  | 'idle'
  | 'connecting'
  | 'ringing'
  | 'connected'
  | 'on-hold'
  | 'ended'
  | 'transferring'
  | 'transferred'
  | 'answered-elsewhere'
  | 'canceled'
  | 'rejected'
  | 'ignored'
  | 'error';

export type CallDirection = 'inbound' | 'outbound';

/**
 * Statuses meaning the agent is actively occupied by this call — including
 * 'transferring' (agent may still be live in the conference while the target
 * rings). Every "is a call in progress?" filter must use this so a mid-transfer
 * call can't be treated as finished (e.g. auto-dialing a second call).
 */
export const LIVE_CALL_STATUSES: readonly CallStatus[] = ['connected', 'connecting', 'ringing', 'on-hold', 'transferring'];

export function isLiveCallStatus(status: CallStatus): boolean {
  return LIVE_CALL_STATUSES.includes(status);
}

/** Whether a call is routed via PSTN or WhatsApp VoIP */
export type CallChannel = 'pstn' | 'whatsapp';

export type SoftphoneState = 'unregistered' | 'registering' | 'registered' | 'error';

// ── Active Call ─────────────────────────────────────────────────────

export interface ActiveCall {
  /** UI-level call ID (may differ from Twilio CallSid until connection is established) */
  id: string;
  /** Actual Twilio CallSid once the connection is accepted/established */
  tpaCallId: string | null;
  direction: CallDirection;
  status: CallStatus;
  /** The customer's phone number (external party) */
  customerNumber: string;
  /** The company caller ID used for this call */
  companyCallerId: string;
  startTime: number | null;
  /** Duration in seconds, updated by timer */
  duration: number;
  isMuted: boolean;
  isRecording: boolean;
  isOnHold: boolean;
  /** True while a hold/resume REST transition is in flight (disables the button) */
  holdPending?: boolean;
  /** Conference room name once the legs have been moved out of the <Dial> bridge for hold */
  holdRoom?: string | null;
  /** The customer leg's CallSid (the other side of the bridge), resolved on first hold */
  customerCallSid?: string | null;
  /** Conference SID, resolved lazily once the hold conference is in-progress */
  holdConferenceSid?: string | null;
  /** Whether the hold conference has been started (agent joined with startConferenceOnEnter) */
  holdConferenceStarted?: boolean;
  /** Conference SID the call lives in once extra people were added (conference call) */
  conferenceSid?: string | null;
  /** People added to this call on top of the customer (conference call) */
  conferenceParticipants?: ConferenceParticipant[];
  /** True while an "add person" request is in flight */
  addingParticipant?: boolean;
  errorMessage?: string;
  /** Whether this call is via PSTN or WhatsApp VoIP */
  callChannel?: CallChannel;
}

/** A person added into a live call (conference call) */
export interface ConferenceParticipant {
  /** Twilio CallSid of the added person's leg */
  callSid: string;
  /** What the agent typed: phone number or agent email */
  target: string;
  /** What was actually dialled: E.164 number or 'client:<email>' (used to spot duplicates) */
  dialTo: string;
  status: 'ringing' | 'joined';
}

/**
 * Whether CallBar shows this call. Inbound ringing calls belong to the
 * incoming-call toast; a canceled inbound call is hidden unless it failed, so
 * its error stays readable. The AppCursor call host frame uses the same rule
 * to decide when it is visible — anything else leaves an empty frame on screen.
 */
export function isCallBarCall(call: ActiveCall, dismissedCallIds: ReadonlySet<string>): boolean {
  if (dismissedCallIds.has(call.id)) return false;
  if (call.direction === 'inbound' && (call.status === 'ringing' || (call.status === 'canceled' && !call.errorMessage))) {
    return false;
  }
  return true;
}

// ── Call History ─────────────────────────────────────────────────────

export interface CallHistoryItem {
  id: string;
  customerNumber: string;
  companyCallerId: string;
  isIncoming: boolean;
  duration: string;
  status: string;
  time: string;
  price: string;
  agentEmail: string | null;
  /** Parent call SID (for child call legs) */
  parentCallSid?: string;
  /** Every leg of this conversation (customer leg + one per agent that
   *  handled it). Notes are stored per leg, so reading a call's notes means
   *  reading all of these. */
  legSids?: string[];
  /** The leg(s) the viewer was personally on — Twilio dials an agent as
   *  `client:<their email>`. Notes they wrote during the call live on their
   *  own leg, so this is where to look for them. Empty when the viewer never
   *  handled the call, or when the identity could not be matched. */
  myLegSids?: string[];
  /** Whether this call was via WhatsApp VoIP */
  callChannel?: CallChannel;
}

export interface CallNote {
  id: string;
  text: string;
  authorEmail: string;
  authorName: string;
  createdAt: number;
}

/** Metadata for a call stored in Firestore UA_CALLS_INBOX/{callId} */
export interface CallMetadata {
  notesCount: number;
  label: string | null;
  assignee: string | null;
  customerNumber?: string;
  /** Legs added to this call by a transfer, recorded when the transfer
   *  completes. Twilio gives a participant-dialled leg no link back to the
   *  customer call, so without this a transferred-to agent's notes — stored
   *  under their own leg — are unreachable from call history. */
  transferLegs?: Array<{ sid: string; agent: string }>;
}

/** A call label definition */
export interface CallLabel {
  value: string;
  label: string;
  color: string;     // hex color e.g. "#3b82f6"
  isCustom?: boolean;
}

/** Predefined color palette for label picker */
export const LABEL_COLOR_PALETTE = [
  '#3b82f6', // blue
  '#ef4444', // red
  '#10b981', // emerald
  '#f59e0b', // amber
  '#8b5cf6', // violet
  '#06b6d4', // cyan
  '#f97316', // orange
  '#ec4899', // pink
  '#64748b', // slate
  '#84cc16', // lime
] as const;

/** Default call labels */
export const DEFAULT_CALL_LABELS: CallLabel[] = [
  { value: 'follow-up', label: 'Follow Up', color: '#3b82f6' },
  { value: 'important', label: 'Important', color: '#ef4444' },
  { value: 'resolved', label: 'Resolved', color: '#10b981' },
  { value: 'callback', label: 'Callback', color: '#f59e0b' },
  { value: 'spam', label: 'Spam', color: '#64748b' },
  { value: 'sales', label: 'Sales', color: '#8b5cf6' },
  { value: 'support', label: 'Support', color: '#06b6d4' },
  { value: 'billing', label: 'Billing', color: '#f97316' },
];

export interface CallRecording {
  id: string;
  duration: string;
  url: string;
}

// ── Caller ID ───────────────────────────────────────────────────────

export interface CallerID {
  /** Phone number SID */
  id: string;
  /** Friendly name or phone number display */
  label: string;
  /** E.164 phone number (or whatsapp:+XXX for WhatsApp senders) */
  value: string;
  /** Link to Twilio console for this number */
  configureUrl?: string;
  /** Channel type — SMS/voice phone numbers vs WhatsApp senders */
  channel?: 'SMS' | 'WHATSAPP';
}

/** A voice-capable phone number or WhatsApp sender */
export interface VoiceNumber {
  /** Phone number SID or WhatsApp sender SID */
  sid: string;
  /** E.164 phone number (or whatsapp:+XXX for WhatsApp senders) */
  phoneNumber: string;
  /** Friendly name */
  friendlyName: string;
  /**
   * Whether incoming call handling is enabled for this number (TwiML app set).
   * All voice-capable numbers are always available as caller IDs for outgoing calls.
   */
  linked: boolean;
  /** Current voice application SID (may be different TwiML app or empty) */
  voiceApplicationSid: string | null;
  /** Channel type — SMS phone numbers vs WhatsApp senders */
  channel?: 'SMS' | 'WHATSAPP';
}

// ── Ring Groups ─────────────────────────────────────────────────────

export interface RingGroup {
  id: string;
  name: string; // e.g. "Sales", "Support"
  agents: string[]; // agent emails
  ringStrategy: 'simultaneous' | 'sequential';
  ringTimeout: number; // seconds per agent (for sequential)
}

// ── Call Status Config ──────────────────────────────────────────────

export interface CallStatusConfig {
  /** Which action buttons to show during this status */
  showOptions: CallAction[];
  /** Status text displayed in the UI */
  displayText: string;
  /** Whether the call timer is running */
  callStarted: boolean;
  /** Whether the call has ended */
  callEnded: boolean;
}

export type CallAction = 'end' | 'hold' | 'record' | 'mute' | 'keypad' | 'transfer' | 'conference';

/** Status configuration map — defines UI behavior for each call status */
export const CALL_STATUS_CONFIGS: Record<CallStatus, CallStatusConfig> = {
  idle: {
    showOptions: [],
    displayText: '',
    callStarted: false,
    callEnded: false,
  },
  connecting: {
    showOptions: ['end'],
    displayText: 'Connecting...',
    callStarted: false,
    callEnded: false,
  },
  ringing: {
    showOptions: ['end'],
    displayText: 'Ringing...',
    callStarted: false,
    callEnded: false,
  },
  connected: {
    showOptions: ['end', 'hold', 'record', 'mute', 'transfer', 'conference', 'keypad'],
    displayText: 'Connected',
    callStarted: true,
    callEnded: false,
  },
  'on-hold': {
    // While held, the agent leg is parked (bridged to nobody): transfer would
    // redirect the parked leg and hang the customer up, recording captures
    // silence, keypad digits go nowhere. Only end / resume / mute are safe.
    showOptions: ['end', 'hold', 'mute'],
    displayText: 'On Hold',
    callStarted: true,
    callEnded: false,
  },
  ended: {
    showOptions: [],
    displayText: 'Call Ended',
    callStarted: false,
    callEnded: true,
  },
  transferring: {
    showOptions: [],
    displayText: 'Transferring…',
    callStarted: false,
    callEnded: false,
  },
  transferred: {
    showOptions: [],
    displayText: 'Call Forwarded',
    callStarted: false,
    callEnded: true,
  },
  'answered-elsewhere': {
    showOptions: [],
    displayText: 'Answered by another agent',
    callStarted: false,
    callEnded: true,
  },
  canceled: {
    showOptions: [],
    displayText: 'Canceled',
    callStarted: false,
    callEnded: true,
  },
  rejected: {
    showOptions: [],
    displayText: 'Declined',
    callStarted: false,
    callEnded: true,
  },
  ignored: {
    showOptions: [],
    displayText: 'Ignored',
    callStarted: false,
    callEnded: true,
  },
  error: {
    showOptions: [],
    displayText: 'Error',
    callStarted: false,
    callEnded: true,
  },
};

// ── Voice Handler Events ────────────────────────────────────────────

export type VoiceEventType =
  | 'device-registered'
  | 'device-registering'
  | 'device-unregistered'
  | 'device-error'
  | 'device-destroyed'
  | 'token-will-expire'
  | 'incoming-call'
  | 'call-accepted'
  | 'call-connecting'
  | 'call-ringing'
  | 'call-connected'
  | 'call-disconnected'
  | 'call-canceled'
  | 'call-rejected'
  | 'call-error'
  | 'call-muted'
  | 'call-unmuted'
  | 'call-id-updated';

export interface VoiceEvent {
  type: VoiceEventType;
  callId?: string;
  oldCallId?: string;
  newCallId?: string;
  customerNumber?: string;
  companyCallerId?: string;
  direction?: CallDirection;
  errorMessage?: string;
  /** Whether this call is via WhatsApp VoIP */
  callChannel?: CallChannel;
}
